import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import rehypeExternalLinks from 'rehype-external-links'
import { useQuery } from '@tanstack/react-query'

import { BinderListEmptyState } from './BinderListEmptyState'
import { LocalStorageKeys } from '../../../../types/local-storage'
import { IconChoices } from '../../../common/Icon'
import { IconToggle } from '../../../common/IconToggle'
import { reverseUrl, ViewNames } from '../../../../navigation/reverseUrl'

import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { Breadcrumb, BreadcrumbItemData } from '../../../common/Breadcrumb'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { BinderListItem } from '../BinderListItem'
import { IBinderModel } from '../../../../interfaces/models/binder.interfaces'
import { useModal } from '../../../../hooks/useModal'
import { Image } from '../../../common/Image'
import { ImagePreviewModal } from '../../../common/ImagePreviewModal'
import { binderQueries } from '../../../../api/binder'
import { CustomDragLayer } from '../CustomDragLayer'

interface BinderListProps {
  documentRoute: ViewNames
  rootRoute: ViewNames
  documents: IBinderModel[]
  binder: IBinderModel[]
  currentDocument: IBinderModel | undefined
  noSearchResults: boolean
  isAdminView: boolean
  folderId?: string
  unitId?: string
  binderId: string
  section?: string
}

export const BinderList = ({
  documentRoute,
  rootRoute,
  documents,
  binder,
  noSearchResults,
  currentDocument,
  folderId,
  unitId,
  isAdminView,
  binderId,
  section,
}: BinderListProps): ReactNode => {
  const { society } = useCurrentSociety()
  const { translate } = useAppTranslation()
  const {
    show: showImagePreviewModal,
    close: closeImagePreviewModal,
    open: openImagePreviewModal,
  } = useModal()
  const [isListMode, setIsListMode] = useState(
    localStorage.getItem(LocalStorageKeys.DOCUMENTS_LIST_MODE)
  )
  const [showFullDescription, setShowFullDescription] = useState(false)
  const [isTextTruncated, setIsTextTruncated] = useState(false)
  const descriptionRef = useRef<HTMLDivElement>(null)
  const { data: binders } = useQuery(
    binderQueries.userHasBinders(society?._id || '')
  )

  useEffect(() => {
    const checkTruncation = (): void => {
      if (descriptionRef.current) {
        const element = descriptionRef.current
        setIsTextTruncated(element.scrollHeight > element.clientHeight)
      }
    }

    checkTruncation()
  }, [currentDocument?.description, showFullDescription])

  const rootLabel = useMemo(() => {
    if (isAdminView) return translate('binderPickerView.title')
    if (binders && binders.length > 1) return translate('unitPickerView.title')
    return translate('binderListView.title')
  }, [isAdminView, binders, translate])

  const hasChildDocuments = useMemo(
    () =>
      documents.some((doc) =>
        folderId ? doc.parentId === folderId : doc.parentId === null
      ),
    [documents, folderId]
  )

  const onChangeListMode = (_isListMode: boolean): void => {
    const updatedValue = _isListMode ? 'list' : 'card'
    localStorage.setItem(LocalStorageKeys.DOCUMENTS_LIST_MODE, updatedValue)
    setIsListMode(updatedValue)
  }

  const getRootPath = (): string => {
    return reverseUrl(rootRoute, { id: society?._id, unitId })
  }

  const getDocumentPath = (docId: string): string => {
    return reverseUrl(documentRoute, {
      id: society?._id,
      unitId,
      documentId: docId,
    })
  }

  const findAllParents = (
    currentDoc: IBinderModel,
    previousParents: IBinderModel[]
  ): IBinderModel[] => {
    if (!currentDoc?.parentId) {
      return previousParents
    }
    const parent = (binder || []).find(
      (doc) => doc._id === currentDoc.parentId
    ) as IBinderModel
    if (!parent) {
      return previousParents
    }
    return findAllParents(parent, [parent, ...previousParents])
  }

  const getAllParentDocuments = (): IBinderModel[] => {
    if (folderId) {
      if (!currentDocument) return []

      const mockDocument = {
        parentId: currentDocument._id,
      } as IBinderModel
      return findAllParents(mockDocument, [])
    }
    return []
  }

  const getAllDocumentBreadcrumbs = (): BreadcrumbItemData[] => {
    const allParentDocuments = getAllParentDocuments()
    const parentBreadcrumbs = allParentDocuments.map((document) => ({
      _id: document._id,
      url: getDocumentPath(document._id),
      title: document.name || document.filename,
      dropParentId: document._id,
    }))
    const rootBreadcrumb = {
      _id: 'root',
      url: getRootPath(),
      title: translate('binderListView.title'),
      dropParentId: null,
    }
    if ((binders && binders.length > 1) || isAdminView) {
      const unitListBreadcrumb = {
        _id: 'unit-list',
        url: reverseUrl(
          section === 'admin' ? 'management:binder' : 'society:binder',
          { id: society?._id }
        ),
        title: translate('unitPickerView.title'),
        dropParentId: null,
      }
      return [unitListBreadcrumb, rootBreadcrumb, ...parentBreadcrumbs]
    }
    return [rootBreadcrumb, ...parentBreadcrumbs]
  }

  const handleFullDescriptionToggle = (): void => {
    setShowFullDescription((prev) => !prev)
  }

  const breadcrumbs = getAllDocumentBreadcrumbs()

  const _isListMode = isListMode === 'list'

  const shouldShowViewMore =
    currentDocument?.description && (isTextTruncated || showFullDescription)

  return (
    <DndProvider backend={HTML5Backend}>
      <CustomDragLayer />
      <div className="flex w-full flex-col space-y-2">
        <div className="flex justify-between">
          <Breadcrumb
            items={breadcrumbs}
            rootIconSize={18}
            rootIcon={IconChoices.BOOK}
            rootLabel={rootLabel}
            droppableType="binder"
            dropEnabled={isAdminView}
            dropStartIndex={
              (binders && binders.length > 1) || isAdminView ? 1 : 0
            }
            disableDropOnLastItem
          />
        </div>
        <div className="flex justify-between gap-2 items-end">
          <div
            className={
              currentDocument?.thumbnail
                ? `flex flex-row gap-8 w-full mb-8`
                : 'relative w-full mb-8'
            }
          >
            {currentDocument?.thumbnail && (
              <div>
                <Image
                  url={currentDocument?.thumbnail}
                  onClick={openImagePreviewModal}
                  className="w-[220px] aspect-video"
                  style={{
                    maxWidth: '220px',
                    maxHeight: 'auto',
                    height: 'auto',
                  }}
                />
              </div>
            )}
            <div
              className={`w-4/5 ${shouldShowViewMore ? 'mb-6' : ''} flex flex-col justify-between`}
            >
              <div
                ref={descriptionRef}
                className={!showFullDescription ? 'line-clamp-[10]' : ''}
              >
                <ReactMarkdown
                  className="prose"
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  rehypePlugins={[[rehypeExternalLinks, { target: 'blank' }]]}
                  components={{
                    // eslint-disable-next-line react/no-unstable-nested-components
                    p: ({ ...props }) => (
                      <p
                        className="break-words mb-2 mt-2 first:mt-0"
                        {...props}
                      />
                    ),
                  }}
                >
                  {currentDocument?.description}
                </ReactMarkdown>
              </div>
              {shouldShowViewMore && (
                <button
                  className="pt-2 text-sm cursor-pointer text-brandGreen self-start"
                  onClick={handleFullDescriptionToggle}
                >
                  {showFullDescription
                    ? translate('common.showLess')
                    : translate('common.showMore')}
                </button>
              )}
            </div>
          </div>
          {hasChildDocuments && (
            <IconToggle
              isFirstSelected={_isListMode}
              firstIcon={IconChoices.LIST}
              secondIcon={IconChoices.GRID}
              onChange={onChangeListMode}
            />
          )}
        </div>

        {documents.length === 0 && !currentDocument?.description ? (
          <BinderListEmptyState
            folderId={folderId}
            documentName={breadcrumbs[breadcrumbs.length - 1].title}
            noSearchResults={noSearchResults}
          />
        ) : (
          <div
            className={`${
              _isListMode ? 'flex flex-col' : 'flex flex-wrap gap-6'
            }`}
          >
            {documents.map((document) => (
              <BinderListItem
                isListMode={_isListMode}
                key={document._id}
                document={document}
                binder={binder}
                documentRoute={documentRoute}
                unitId={unitId}
                isAdminView={isAdminView}
                binderId={binderId}
              />
            ))}
          </div>
        )}
      </div>
      <ImagePreviewModal
        showPreviewModal={showImagePreviewModal}
        url={currentDocument?.thumbnail || ''}
        onClose={closeImagePreviewModal}
        text={translate('createUpdateFolderForm.labels.thumbnail')}
      />
    </DndProvider>
  )
}
